import React from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import Error404 from '../components/Errors/Error404';
import { matchPath } from 'react-router-dom';
import { useSessionExpiration } from '../hooks/useSessionExpiration';
import ExpiredSessionModal from '../components/Expired/ExpiredSessionModal';
import { isUserSuperAdmin } from '../utils/userTier';
import Loading from '../components/Loading';
import { ROUTES } from './routePaths';

const ProtectedRoute: React.FC = () => {
    const { user, isAuthenticated, loading: authLoading, logout } = useAuth();
    const { permissions, loading: permissionsLoading } = usePermissions();
    const location = useLocation();
    const navigate = useNavigate();
    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();

    const handleCloseExpiredModal = () => {
        setIsSessionExpired(false);
        logout(); 
        navigate('/', { replace: true });
    };

    if (authLoading || permissionsLoading) {
        const isClientRoute = [
            ROUTES.reservaYa, ROUTES.misCitas, ROUTES.solicitudesReserva,
            ROUTES.perfil, ROUTES.miDashboard, ROUTES.parati, ROUTES.miCorrespondencia,
        ].some(path => matchPath({ path, end: false }, location.pathname));
        return isClientRoute
            ? <Loading text="Cargando..." />
            : <div aria-busy="true" style={{ minHeight: '100dvh', background: '#fff' }} />;
    }

    if (!isAuthenticated) {
        return <Navigate to="/" state={{ from: location }} replace />;
    }

    // ── SUPERADMIN: Acceso total e irrestricto a todas las rutas protegidas ──
    if (isUserSuperAdmin(user)) {
        return (
            <>
                <ExpiredSessionModal
                    isOpen={isSessionExpired}
                    onClose={handleCloseExpiredModal}
                />
                <Outlet />
            </>
        );
    }

    const path = location.pathname;
    const matchedPattern = Object.keys(permissions)
        .find(pattern => matchPath({ path: pattern, end: false }, path));
    const allowedRoles = matchedPattern ? permissions[matchedPattern] : null;

    // Rutas de administración de Landing (Categorías, Blogs, Empresas, Ramas, Eventos)
    const landingAdminRoutes = [
        '/blog-categoria',
        '/blog-post',
        '/empresas-confianza',
        '/ramas-profesionales',
        '/eventos-admin',
    ];
    const isLandingAdmin = landingAdminRoutes.some(r => path.startsWith(r));

    const userRole = (user?.role || '').toLowerCase().trim();
    const isStaffOrAdmin = Boolean(
        user?.user_level_id === 1 || 
        user?.user_level_id === 2 || 
        userRole === 'administrador' || 
        userRole === 'personal' || 
        userRole === 'admin' || 
        userRole === 'staff'
    );

    if (!allowedRoles && !isLandingAdmin) return <Error404 />;
    
    const roleAllowed = allowedRoles
        ? allowedRoles.some(r => r.toLowerCase().trim() === userRole)
        : false;

    const isSpecialAllowed = Boolean(
        (path.startsWith('/mi-correspondencia') && (user?.es_domiciliado || user?.tiene_correspondencia_habilitada)) ||
        (isLandingAdmin && isStaffOrAdmin)
    );

    if (!roleAllowed && !isSpecialAllowed) return <Error404 />;

    return (
        <>
            <ExpiredSessionModal
                isOpen={isSessionExpired}
                onClose={handleCloseExpiredModal}
            />
            <Outlet />
        </>
    );
};

export default ProtectedRoute;
